import type { ReactNode } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useState,
  useMemo,
} from 'react';

export enum DrawerType {
  FULL_HEIGHT_DRAWER = 'FULL_HEIGHT_DRAWER',
  CONTAINED_DRAWER = 'CONTAINED_DRAWER',
}

interface DrawerState {
  content: ReactNode | null;
  drawerType?: DrawerType;
  isOpen: boolean;
}

export interface ActionDrawerContextType {
  openDrawer: (
    content: ReactNode,
    options?: { drawerType?: DrawerType },
  ) => void;
  closeDrawer: () => void;
  drawerState: DrawerState;
}

const ActionDrawerContext = createContext<ActionDrawerContextType | null>(null);

interface ActionDrawerProviderProps {
  children: ReactNode;
}

export const ActionDrawerProvider = ({
  children,
}: ActionDrawerProviderProps) => {
  const [drawerState, setDrawerState] = useState<DrawerState>({
    content: null,
    drawerType: undefined,
    isOpen: false,
  });

  const openDrawer = useCallback(
    (content: ReactNode, options?: { drawerType?: DrawerType }) => {
      setDrawerState({
        content,
        drawerType: options?.drawerType || DrawerType.CONTAINED_DRAWER,
        isOpen: true,
      });
    },
    [],
  );

  const closeDrawer = useCallback(() => {
    setDrawerState((prev) => ({ ...prev, isOpen: false }));
  }, []);

  const value = useMemo(
    () => ({
      openDrawer,
      closeDrawer,
      drawerState,
    }),
    [openDrawer, closeDrawer, drawerState],
  );

  return (
    <ActionDrawerContext.Provider value={value}>
      {children}
    </ActionDrawerContext.Provider>
  );
};

export const useActionDrawer = (): ActionDrawerContextType => {
  const context = useContext(ActionDrawerContext);
  if (!context) {
    throw new Error(
      'useActionDrawer must be used within an ActionDrawerProvider',
    );
  }
  return context;
};
